import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Toaster } from '@aitoken/ui/components/sonner';
import { TooltipProvider } from '@aitoken/ui/components/tooltip';
import { setUnauthorizedHandler } from '@/lib/api';
import { configureDisplay } from '@/lib/format';
import { applySiteDefault } from '@/i18n/runtime';
import { meQuery, siteQuery } from '@/lib/queries';
import { queryClient } from '@/lib/query-client';
import { NotFoundPage, RouteErrorPage } from '@/pages/not-found';
import { PublicLayout } from '@/components/public-layout';
import { AuthLayout } from '@/pages/auth/auth-layout';

interface RouterContext {
  queryClient: QueryClient;
}

function Root() {
  const { data: site } = useQuery(siteQuery);
  // 站点信息：展示时区（cn 站 Asia/Shanghai），幂等设置；浏览器语言都不支持时按站点兜底语言
  if (site) {
    configureDisplay(site.timezone);
    void applySiteDefault(site.site_id);
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Outlet />
      <Toaster position="top-center" richColors={false} closeButton />
    </TooltipProvider>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFoundPage,
  beforeLoad: ({ context }) => {
    // 站点信息预取：失败时使用兜底，不阻塞公开页面
    void context.queryClient.prefetchQuery(siteQuery);
  },
});

/* ---------------- 公开页面（永不跳转登录） ---------------- */

const publicLayout = createRoute({ getParentRoute: () => rootRoute, id: 'public', component: PublicLayout });

const homeRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/public/home'), 'HomePage'),
});

const modelsRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/models',
  component: lazyRouteComponent(() => import('@/pages/public/models'), 'ModelsPage'),
});

const topupTermsRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/terms/topup',
  component: lazyRouteComponent(() => import('@/pages/public/topup-terms'), 'TopupTermsPage'),
});

const legalRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/legal/$doc',
  component: lazyRouteComponent(() => import('@/pages/public/legal'), 'LegalPage'),
});

const docsIndexRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/docs',
  component: lazyRouteComponent(() => import('@/pages/docs/docs-page'), 'DocsIndexPage'),
});

const docsSlugRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/docs/$slug',
  component: lazyRouteComponent(() => import('@/pages/docs/docs-page'), 'DocsSlugPage'),
});

const portraitCallbackRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/portrait-callback',
  component: lazyRouteComponent(() => import('@/pages/public/portrait-callback'), 'PortraitCallbackPage'),
});

/* ---------------- 账号 ---------------- */

const authLayout = createRoute({ getParentRoute: () => rootRoute, id: 'auth', component: AuthLayout });

function safeRedirect(value: unknown): string | undefined {
  // 只接受站内路径，防止开放重定向
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value : undefined;
}

const loginRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: safeRedirect(search.redirect),
  }),
  component: lazyRouteComponent(() => import('@/pages/auth/login'), 'LoginPage'),
});

const registerRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/register',
  validateSearch: (search: Record<string, unknown>): { email?: string; ref?: string; ch?: string } => ({
    email: typeof search.email === 'string' ? search.email.slice(0, 254) : undefined,
    ref: typeof search.ref === 'string' ? search.ref.slice(0, 32) : undefined,
    // 渠道推广码（分销渠道链接 /register?ch=CODE）
    ch: typeof search.ch === 'string' ? search.ch.slice(0, 32) : undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/auth/register'), 'RegisterPage'),
});

const forgotRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('@/pages/auth/password'), 'ForgotPasswordPage'),
});

const resetRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/reset-password',
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/auth/password'), 'ResetPasswordPage'),
});

/* ---------------- 控制台（唯一需要登录的区域） ---------------- */

const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/console',
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { me };
  },
  component: lazyRouteComponent(() => import('@/pages/console/console-layout'), 'ConsoleLayout'),
});

const overviewRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/console/overview'), 'OverviewPage'),
});

const keysRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/keys',
  component: lazyRouteComponent(() => import('@/pages/console/keys'), 'KeysPage'),
});

export interface AssetsSearch {
  group?: string;
  kind?: 'image' | 'video' | 'audio';
  status?: string;
  q?: string;
  view?: 'grid' | 'list';
}

const assetsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/assets',
  validateSearch: (s: Record<string, unknown>): AssetsSearch => ({
    group: typeof s.group === 'string' && s.group ? s.group : undefined,
    kind: s.kind === 'image' || s.kind === 'video' || s.kind === 'audio' ? s.kind : undefined,
    status: typeof s.status === 'string' && s.status ? s.status : undefined,
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    view: s.view === 'list' ? 'list' : undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/console/assets'), 'AssetsPage'),
});

export interface TasksSearch {
  tab?: 'logs';
  status?: string;
  model?: string;
  key?: string;
  from?: string;
  to?: string;
  log_status?: string;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

const tasksRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/tasks',
  validateSearch: (s: Record<string, unknown>): TasksSearch => ({
    tab: s.tab === 'logs' ? 'logs' : undefined,
    status: str(s.status),
    model: str(s.model),
    key: str(s.key),
    from: str(s.from),
    to: str(s.to),
    log_status: str(s.log_status),
  }),
  component: lazyRouteComponent(() => import('@/pages/console/tasks'), 'TasksPage'),
});

const taskDetailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/tasks/$id',
  component: lazyRouteComponent(() => import('@/pages/console/task-detail'), 'TaskDetailPage'),
});

const playgroundRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/playground',
  component: lazyRouteComponent(() => import('@/pages/console/playground'), 'PlaygroundPage'),
});

const billingRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/billing',
  validateSearch: (s: Record<string, unknown>): { type?: string } => ({ type: str(s.type) }),
  component: lazyRouteComponent(() => import('@/pages/console/billing'), 'BillingPage'),
});

const referralsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/referrals',
  component: lazyRouteComponent(() => import('@/pages/console/referrals'), 'ReferralsPage'),
});

/** 渠道后台（docs/12）：只有渠道商的后台账号能看到入口；接口按成员身份鉴权 */
const resellerRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/reseller',
  component: lazyRouteComponent(() => import('@/pages/console/reseller'), 'ResellerPage'),
});

const settingsRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('@/pages/console/settings'), 'SettingsPage'),
});

export const routeTree = rootRoute.addChildren([
  publicLayout.addChildren([
    homeRoute,
    modelsRoute,
    topupTermsRoute,
    legalRoute,
    docsIndexRoute,
    docsSlugRoute,
    portraitCallbackRoute,
  ]),
  authLayout.addChildren([loginRoute, registerRoute, forgotRoute, resetRoute]),
  consoleRoute.addChildren([
    overviewRoute,
    keysRoute,
    assetsRoute,
    tasksRoute,
    taskDetailRoute,
    playgroundRoute,
    billingRoute,
    referralsRoute,
    resellerRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  scrollRestoration: true,
});

// 受保护页面收到 401：保留当前路径，登录后返回
setUnauthorizedHandler(() => {
  const { pathname, searchStr } = router.state.location;
  if (!pathname.startsWith('/console')) return;
  void router.navigate({ to: '/login', search: { redirect: `${pathname}${searchStr}` }, replace: true });
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
